import { PrimaryButton } from "components/designV2/buttons/primaryButton";
import { SecondaryButton } from "components/designV2/buttons/secondaryButton";
import {
  Dropdown,
  DropdownOption,
} from "components/designV2/dropdown/dropdown";
import { InputField } from "components/designV2/inputfield/inputfield";
import { Modal } from "components/designV2/modal/modal";
import { Text } from "components/designV2/text/text";
import { ColorV2 } from "config/colors";
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { useCompanyStore } from "web-apps/company/stores/companyStore/companyStore";
import { useOrganizationalStructureStore } from "web-apps/company/stores/organizationalStructureStore/organizationalStructureStore";

type Props = {
  setShowEditModal: Dispatch<SetStateAction<boolean>>;
  showEditModal: boolean;
  type: "Entity" | "Unit" | "Team";
  id?: number;
  parentId?: number;
  name: string;
};

type ResultState = "saved" | "deleted" | "error" | null;

const DELETE_BUTTON_STYLES = {
  textColor: ColorV2.NegativeRed,
  borderColor: ColorV2.NegativeRed,
  iconColor: ColorV2.NegativeRed,
  hoverBackgroundColor: "#fce9e9",
  activeBackgroundColor: "rgba(211, 78, 78, 0.2)",
};

export const EditModal: React.FC<Props> = ({
  setShowEditModal,
  showEditModal,
  type,
  id,
  parentId,
  name: initialName,
}) => {
  const { t } = useTranslation();
  const [companyState] = useCompanyStore();
  const [state, organizationalStructureActions] =
    useOrganizationalStructureStore();

  const companyId = companyState.company?.id;
  const isTeam = type === "Team";
  const isMoveBetweenUnits = isTeam && parentId === -1;
  const itemLabel = t(`OrganizationalStructure.${type}`).toLowerCase();

  const [name, setName] = useState(initialName);
  const [isEditorOpen, setIsEditorOpen] = useState(showEditModal);
  const [resultState, setResultState] = useState<ResultState>(null);
  const [showDeleteAlert, setShowDeleteAlert] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedUnit, setSelectedUnit] = useState<number | undefined>();

  const mountedRef = useRef(true);
  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!showEditModal) return;

    setName(initialName);
    setSelectedUnit(undefined);
    setLoading(false);
    setResultState(null);
    setShowDeleteAlert(false);
    setIsEditorOpen(true);
  }, [initialName, showEditModal]);

  const closeAndUnmount = useCallback(() => {
    setIsEditorOpen(false);
    setShowDeleteAlert(false);
    setLoading(false);
    setResultState(null);
    setShowEditModal(false);
  }, [setShowEditModal]);

  const unitOptions = useMemo<DropdownOption[]>(() => {
    return (
      state.structure?.entities?.flatMap((entity) =>
        entity.units
          ?.filter((unit) => unit.name !== "Unhandled")
          ?.map((unit) => ({
            value: unit.id.toString(),
            label: `${entity.name} - ${unit.name}`,
          })),
      ) ?? []
    );
  }, [state.structure]);

  const canSave = useMemo(() => {
    const nameChanged = name.trim() !== initialName.trim();
    const hasSelectedUnit = selectedUnit !== undefined;
    const hasChanges = nameChanged || (isMoveBetweenUnits && hasSelectedUnit);

    return !loading && hasChanges && (!isMoveBetweenUnits || hasSelectedUnit);
  }, [initialName, isMoveBetweenUnits, loading, name, selectedUnit]);

  const handleSaveChanges = useCallback(() => {
    if (!companyId || id == null) return;

    setLoading(true);

    let updatePromise: Promise<unknown>;

    if (type === "Team" && parentId != null) {
      updatePromise = organizationalStructureActions.updateTeam(
        companyId,
        id,
        name,
        isMoveBetweenUnits ? selectedUnit : undefined,
      );
    } else if (type === "Unit" && parentId != null) {
      updatePromise = organizationalStructureActions.updateUnit(
        companyId,
        parentId,
        id,
        name,
      );
    } else {
      updatePromise = organizationalStructureActions.updateEntity(
        companyId,
        id,
        name,
      );
    }

    updatePromise
      .then(() => {
        if (!mountedRef.current) return;
        setLoading(false);
        setIsEditorOpen(false);
        setResultState("saved");
      })
      .catch(() => {
        if (!mountedRef.current) return;
        setLoading(false);
        setResultState("error");
      });
  }, [
    companyId,
    id,
    isMoveBetweenUnits,
    name,
    organizationalStructureActions,
    parentId,
    selectedUnit,
    type,
  ]);

  const handleDelete = useCallback(() => {
    if (!companyId || id == null) return;

    setLoading(true);

    let deletePromise: Promise<unknown>;

    if (type === "Team" && parentId != null) {
      deletePromise = organizationalStructureActions.deleteTeam(
        companyId,
        parentId,
        id,
      );
    } else if (type === "Unit" && parentId != null) {
      deletePromise = organizationalStructureActions.deleteUnit(
        companyId,
        parentId,
        id,
      );
    } else {
      deletePromise = organizationalStructureActions.deleteEntity(
        companyId,
        id,
      );
    }

    deletePromise
      .then(() => {
        if (!mountedRef.current) return;
        setLoading(false);
        setShowDeleteAlert(false);
        setIsEditorOpen(false);
        setResultState("deleted");
      })
      .catch(() => {
        if (!mountedRef.current) return;
        setLoading(false);
        setShowDeleteAlert(false);
        setResultState("error");
      });
  }, [companyId, id, organizationalStructureActions, parentId, type]);

  const handleResultClose = useCallback(() => {
    if (resultState === "saved" || resultState === "deleted") {
      closeAndUnmount();
      return;
    }

    setResultState(null);
  }, [closeAndUnmount, resultState]);

  const resultLabel =
    resultState === "error"
      ? t("OrganizationalStructure.SomethingWentWrong")
      : resultState === "saved"
        ? t("OrganizationalStructure.Saved")
        : t("OrganizationalStructure.Removed");

  const resultMessage =
    resultState === "error"
      ? t("OrganizationalStructure.SomethingWentWrongInfo")
      : resultState === "saved"
        ? t("OrganizationalStructure.YourChangesHaveBeenSaved")
        : t("OrganizationalStructure.SuccessfullyRemoved", {
            name: initialName,
          });

  return (
    <>
      <Modal
        isOpen={isEditorOpen}
        onClose={closeAndUnmount}
        label={t("OrganizationalStructure.Edit", { name: initialName })}
        size="largeSlim"
        footer={
          <>
            <SecondaryButton
              label={t("OrganizationalStructure.Cancel")}
              disabled={loading}
              onClick={closeAndUnmount}
            />
            <SecondaryButton
              label={t("OrganizationalStructure.Remove", { type: itemLabel })}
              iconName="v2/trash"
              disabled={loading}
              onClick={() => setShowDeleteAlert(true)}
              styleOverrides={DELETE_BUTTON_STYLES}
            />
            <PrimaryButton
              label={t("OrganizationalStructure.SaveChanges")}
              isLoading={loading}
              disabled={!canSave}
              onClick={handleSaveChanges}
            />
          </>
        }
      >
        <Content>
          <Text typography="b2" color={ColorV2.Blue4}>
            {t(`OrganizationalStructure.Create${type}Info`)}
          </Text>

          <Fields>
            <InputField
              label={t("WorkGroup.Name")}
              value={name}
              maxLength={100}
              fullWidth
              onChange={setName}
              onClear={() => setName("")}
            />

            {isMoveBetweenUnits && (
              <Dropdown
                label={t("OrganizationalStructure.Unit")}
                options={unitOptions}
                value={selectedUnit?.toString()}
                placeholder={t("OrganizationalStructure.ChooseUnitToMoveTo")}
                fullWidth
                onChange={(value) => {
                  const nextValue = Number(value);
                  setSelectedUnit(
                    Number.isNaN(nextValue) ? undefined : nextValue,
                  );
                }}
              />
            )}
          </Fields>
        </Content>
      </Modal>

      {showDeleteAlert && (
        <Modal
          isOpen={showDeleteAlert}
          onClose={() => setShowDeleteAlert(false)}
          label={t("OrganizationalStructure.AlertTitle", { name: initialName })}
          size="compact"
          showCloseButton={false}
          footer={
            <>
              <SecondaryButton
                label={t("OrganizationalStructure.Cancel")}
                disabled={loading}
                onClick={() => setShowDeleteAlert(false)}
              />
              <PrimaryButton
                label={t("OrganizationalStructure.Confirm")}
                isLoading={loading}
                onClick={handleDelete}
              />
            </>
          }
        >
          <Text typography="b2" color={ColorV2.Blue1}>
            {t(`OrganizationalStructure.AlertInfo-${type}`, {
              name: initialName,
            })}
          </Text>
        </Modal>
      )}

      {resultState && (
        <Modal
          isOpen
          onClose={handleResultClose}
          label={resultLabel}
          size="compact"
          showCloseButton={false}
          footer={
            <PrimaryButton
              label={t("OrganizationalStructure.Ok")}
              onClick={handleResultClose}
            />
          }
        >
          <Text typography="b2" color={ColorV2.Blue1}>
            {resultMessage}
          </Text>
        </Modal>
      )}
    </>
  );
};

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const Fields = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;
